/**
 * Stripe catalog health (LG-433). Read-only by design — Stripe's dashboard
 * is the editor. This page answers "is every product configured so it can
 * be sold safely?": valid packs alongside the zod-invalid metadata that
 * would otherwise become a payments incident.
 */
import { Button } from '@/components/ui/button'
import { trpc } from '@/lib/trpc'

function formatPrice(unitAmount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    // Whole-dollar amounts render clean; an amount with cents must show
    // them — rounding would misstate what checkout charges.
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
  }).format(unitAmount / 100)
}

// Test-mode objects live under /test on the dashboard; the same id on the
// live path is a 404.
function stripePriceUrl(priceId: string, testMode: boolean): string {
  return `https://dashboard.stripe.com${testMode ? '/test' : ''}/prices/${priceId}`
}

function stripeProductUrl(productId: string, testMode: boolean): string {
  return `https://dashboard.stripe.com${testMode ? '/test' : ''}/products/${productId}`
}

export function CatalogHealthPage() {
  const utils = trpc.useUtils()
  const health = trpc.admin.catalog.health.useQuery()
  const refresh = trpc.admin.catalog.refresh.useMutation({
    onSuccess: () => utils.admin.catalog.health.invalidate(),
  })
  const testMode = health.data?.testMode ?? false

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Stripe catalog</h1>
        <Button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? 'Refreshing…' : 'Refresh from Stripe'}
        </Button>
      </div>

      {refresh.isError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm"
        >
          {`Refresh failed — the table below is cached data${
            health.data
              ? ` from ${new Date(health.data.fetchedAt).toLocaleTimeString()}`
              : ''
          }.`}
        </p>
      )}

      {health.isError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm"
        >
          Couldn't load the catalog.
        </p>
      )}

      {/* Zero or several `starter: true` prices is a catalog-level fault —
          no single row to flag, and the application lanes sell nothing
          until it is fixed in the dashboard. */}
      {health.data?.starterError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm"
        >
          {`Starter pack: ${health.data.starterError}.`}
        </p>
      )}

      {health.data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr className="text-muted-foreground text-left text-xs">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Price ID</th>
                  <th className="px-4 py-3 font-medium">Credits</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Visibility</th>
                  <th className="px-4 py-3 font-medium">Generation</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {health.data.packs.map((pack) => (
                  <tr key={pack.priceId}>
                    <td className="px-4 py-3">
                      {pack.name}
                      {pack.starter && (
                        <span className="bg-primary/10 text-primary ml-2 rounded px-2 py-0.5 text-xs">
                          starter
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={stripePriceUrl(pack.priceId, testMode)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {pack.priceId}
                      </a>
                    </td>
                    <td className="px-4 py-3">{pack.credits}</td>
                    <td className="px-4 py-3">
                      {formatPrice(pack.unitAmount, pack.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-muted rounded px-2 py-0.5 text-xs">
                        {pack.visibility}
                      </span>
                    </td>
                    {/* The product IS the pricing generation; names stay
                        identical across generations by design, so the id +
                        creation date are what tell lineups apart. */}
                    <td className="px-4 py-3 text-xs">
                      <a
                        href={stripeProductUrl(pack.productId, testMode)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {pack.productId}
                      </a>
                      {pack.productCreated != null && (
                        <span className="text-muted-foreground block">
                          {new Date(
                            pack.productCreated * 1000,
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3" aria-label="Valid">
                      ✅
                    </td>
                  </tr>
                ))}
                {health.data.invalid.map((entry) => (
                  <tr key={entry.priceId} className="bg-destructive/5">
                    <td className="px-4 py-3">
                      {entry.name ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={stripePriceUrl(entry.priceId, testMode)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {entry.priceId}
                      </a>
                    </td>
                    <td className="px-4 py-3" colSpan={4}>
                      <span className="text-destructive">{entry.error}</span>
                    </td>
                    <td className="px-4 py-3" aria-label="Invalid">
                      ❌
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {health.data.unmanaged.length > 0 && (
            <details className="text-muted-foreground text-sm">
              <summary className="cursor-pointer">
                {`Unmanaged prices (${health.data.unmanaged.length}) — active in Stripe, not part of this catalog`}
              </summary>
              <ul className="mt-2 space-y-1 pl-4">
                {health.data.unmanaged.map((entry) => (
                  <li key={entry.priceId}>
                    {entry.name ?? '—'}{' '}
                    <a
                      href={stripePriceUrl(entry.priceId, testMode)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {entry.priceId}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <p className="text-muted-foreground text-xs">
            {`Fetched ${new Date(health.data.fetchedAt).toLocaleTimeString()} · cached up to 60s`}
          </p>
        </>
      )}
    </div>
  )
}
